import {
  OperationVariables,
  ApolloQueryResult,
  useSubscription,
  useLazyQuery
} from '@apollo/client'
import { Checkbox, Fade, IconButton, TableSortLabel } from '@material-ui/core'
import RefreshIcon from '@material-ui/icons/Refresh'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import {
  ALL_SETTLEMENT_TRANSACTIONS,
  CALCULATE_ALL_TRANSACTIONS_JOB_SUB
} from '../../graphql/walletTransactions'
import { getTranslate } from '../../localization'
import { SortBy } from '../../types/walletEnums'
import {
  PaymentTransactionFilterInput,
  PaymentTransactionFilterInputWithoutPagination,
  PaymentTransactionResponse,
  SelectedTransactions,
  WalletTransaction
} from '../../types/walletTransaction'
import { WalletTransactionListTableRowDesktop } from './WalletTransactionListTableRowDesktop'
import { WalletTransactionListRowDesktopSkeleton } from './WalletTransactionListTableRowDesktop.Skeleton'
import useStyles from './styles/WalletListTableDesktop.styles'

interface WalletTransactionListTableProps {
  transactionsData: WalletTransaction[]
  loadingForTransactions?: boolean
  setSort: React.Dispatch<React.SetStateAction<SortBy>>
  sort: SortBy
  setpage: React.Dispatch<React.SetStateAction<number>>
  refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<
    ApolloQueryResult<{
      settlementTransactions: PaymentTransactionResponse
    }>
  >
  selectedTransactions: SelectedTransactions
  setselectedTransactions: React.Dispatch<
    React.SetStateAction<SelectedTransactions>
  >
  filters: PaymentTransactionFilterInput
  setwaitingForWebSocketToCalcAllTransactions: React.Dispatch<
    React.SetStateAction<boolean>
  >
}
export const WalletTransactionListTableDesktop: React.FC<
  WalletTransactionListTableProps
> = ({
  transactionsData,
  setSort,
  sort,
  refetch,
  setpage,
  loadingForTransactions,
  selectedTransactions,
  setselectedTransactions,
  filters,
  setwaitingForWebSocketToCalcAllTransactions
}) => {
  const classes = useStyles()
  const [trackingID, setTrackingID] = useState<string | null>(null)
  const [isAllSelected, setisAllSelected] = useState(false)
  const [indeterminate, setIndeterminate] = useState(false)

  const cachedJobResultRef = useRef<{
    filters: PaymentTransactionFilterInputWithoutPagination
    result: {
      totalAmountToPay: number
      transactionIDs: string[]
    }
  } | null>(null)
  const [startJob] = useLazyQuery(ALL_SETTLEMENT_TRANSACTIONS, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const id = data?.allSettlementTransactions?.trackingID
      if (id) setTrackingID(id)
    }
  })
  const { data: allTransactionsToPay } = useSubscription(
    CALCULATE_ALL_TRANSACTIONS_JOB_SUB,
    {
      variables: { trackingID },
      skip: !trackingID
    }
  )

  useEffect(() => {
    const jobData = allTransactionsToPay?.calculateAllTransactionsJobHandler

    if (jobData?.status === 'Completed') {
      const total = jobData.totalAmount
      const ids = jobData.transactionIDs

      // Cache the result along with current filters (without pagination)
      const { page, perPage, ...filtersWithoutPagination } = filters
      cachedJobResultRef.current = {
        filters: filtersWithoutPagination,
        result: {
          totalAmountToPay: total,
          transactionIDs: ids
        }
      }

      setselectedTransactions({
        totalAmountToPay: total,
        transactionIDs: ids
      })
      setisAllSelected(true)
    }
  }, [allTransactionsToPay])

  const isWebSocketLoading = useMemo(() => {
    return (
      !!trackingID &&
      allTransactionsToPay?.calculateAllTransactionsJobHandler?.status !==
        'Completed'
    )
  }, [trackingID, allTransactionsToPay])

  useEffect(() => {
    setwaitingForWebSocketToCalcAllTransactions(isWebSocketLoading)
  }, [isWebSocketLoading])

  useEffect(() => {
    const allIDs =
      allTransactionsToPay?.calculateAllTransactionsJobHandler
        ?.transactionIDs || []
    const selectedIDs = selectedTransactions.transactionIDs

    if (!selectedIDs.length) {
      setIndeterminate(false)
      setisAllSelected(false)
    } else if (
      allIDs.length &&
      selectedIDs.length === allIDs.length &&
      selectedIDs.every((id) => allIDs.includes(id))
    ) {
      setIndeterminate(false)
      setisAllSelected(true)
    } else {
      setIndeterminate(true)
      setisAllSelected(false)
    }
  }, [selectedTransactions, allTransactionsToPay])

  interface HeadCell {
    label: string
    id: string
  }
  const headCells: HeadCell[] = [
    { id: 'time', label: 'Time' },
    ...(transactionsData[0]?.creditorID
      ? [{ id: '2', label: 'Creditor' }]
      : []),
    { id: '3', label: 'R.N' },
    { id: 'amount', label: 'Amount' },
    { id: '5', label: 'Payment Due' },
    { id: '6', label: 'Penalty/Bounce' },
    { id: '7', label: 'Status' },
    { id: '8', label: 'Payment Status' }
  ]
  return (
    <div>
      <div
        className={
          transactionsData[0]?.creditorID
            ? classes.tableHead
            : classes.tableHeadWithoutCreditor
        }>
        <Checkbox
          color="primary"
          checked={isAllSelected}
          indeterminate={indeterminate}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const isChecked = event.target.checked

            if (isChecked) {
              const {
                page: unusedPage,
                perPage: unusedPerPage,
                ...filtersWithoutPagination
              } = filters

              // Compare current filters to cached filters
              const filtersString = JSON.stringify(filtersWithoutPagination)
              const cachedFiltersString = JSON.stringify(
                cachedJobResultRef.current?.filters || {}
              )

              if (
                filtersString === cachedFiltersString &&
                cachedJobResultRef.current
              ) {
                // Use cached result
                const { transactionIDs, totalAmountToPay } =
                  cachedJobResultRef.current.result
                setselectedTransactions({
                  totalAmountToPay,
                  transactionIDs
                })
                setisAllSelected(true)
              } else {
                // Start new job
                startJob({
                  variables: {
                    filters: filtersWithoutPagination,
                    sortBy: 'default'
                  }
                })
              }
            } else {
              setselectedTransactions({
                totalAmountToPay: 0,
                transactionIDs: []
              })
            }
          }}
        />
        {headCells.map((item) => (
          <div key={item.id}>
            <TableSortLabel
              className={classes.tableSortLabel}
              active={item.id === 'time' || item.id === 'amount' ? true : false}
              onClick={() => {
                setSort(
                  item.id === 'time' || item.id === 'amount'
                    ? item.id
                    : 'dectime'
                )

                if (sort === item.id) {
                  setSort(('dec' + item.id) as SortBy)
                }
              }}
              hideSortIcon
              direction={item.id === sort ? 'desc' : 'asc'}>
              {getTranslate(item.label)}
            </TableSortLabel>
          </div>
        ))}

        <div className={classes.refreshButtonContainer}>
          <IconButton
            onClick={() => {
              setpage(1)
              refetch()
            }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </div>
      </div>
      {loadingForTransactions
        ? Array(5)
            .fill('')
            .map((item, index) => (
              <WalletTransactionListRowDesktopSkeleton
                key={`accommodation-skeleton${item}${index.toString()}`}
              />
            ))
        : transactionsData.map((singleTransaction: WalletTransaction) => (
            <Fade in timeout={600} key={singleTransaction.transactionID}>
              <WalletTransactionListTableRowDesktop
                data={singleTransaction}
                checkboxDisabled={isWebSocketLoading}
                {...{ setselectedTransactions, selectedTransactions }}
              />
            </Fade>
          ))}
    </div>
  )
}
